import React from 'react';
import { Box, Button, ButtonProps, useMediaQuery, useTheme } from '@material-ui/core';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { Method } from '../../routePaths';
import { PageWrapper } from '../PageWrapper';
import { FixedButtonWrapper } from '../../modules/warehouse/components/style';
import { ProtectedAction } from '../ProtectedAction';
import { ProtectedActionProps } from '../ProtectedAction/ProtectedAction';
import { AppPermissions } from '../../generated/graphql';

interface WarehousePageWrapperActionProps extends ButtonProps {
    route?: string;
    isRouteExplicit?: boolean;
    isShown?: boolean;
    text?: string;
    startIcon?: React.ReactNode;
    onClick?: () => void;
}

export type WarehousePageWrapperActions = Array<WarehousePageWrapperActionProps>;

interface WarehousePageWrapperProps {
    permissions?: Partial<Pick<AppPermissions, 'backoffice' | 'warehouse'>>;
    actions?: WarehousePageWrapperActions;
    children: React.ReactNode;
    isDesktop?: boolean;
}

const NavigationButton = ({ onClick, ...other }: ButtonProps | ButtonProps<RouterLink>) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

    if (onClick) {
        const buttonProps: ButtonProps = { ...other as ButtonProps, onClick: onClick as ButtonProps['onClick'] };
        return <Button fullWidth={!isDesktop} {...buttonProps} />;
    }
    return <LinkButton fullWidth={!isDesktop} {...other as ButtonProps<RouterLink>} />;
};

const LinkButton = (props: ButtonProps<RouterLink>) => <Button component={RouterLink} {...props} />;

const WarehousePageWrapper = ({
    children,
    permissions,
    actions = [],
}: WarehousePageWrapperProps) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const location = useLocation();

    const buttonsProps = actions.map(({
        route,
        isRouteExplicit,
        variant = 'contained',
        color = 'primary',
        startIcon = <AddCircleIcon />,
        onClick,
    }) => {
        const buttonProps: ProtectedActionProps = {
            startIcon,
            variant,
            color,
            permissions,
            onClick,
            component: NavigationButton,
        };

        const anchorProps = {
            ...buttonProps,
            to: {
                pathname: isRouteExplicit ? route : `${route}${Method.CREATE}`,
                state: { background: location }
            },
        };

        return onClick ? buttonProps : anchorProps;
    });

    const isAnyButtonShown = actions.some(({ isShown = true }) => isShown);

    return (
        <PageWrapper noPadding>
            {isDesktop && isAnyButtonShown && (
                <Box width="100%" m={3} display="flex" justifyContent="flex-end">
                    {actions.map(({ isShown = true, text }, index) => (
                        isShown ? (
                            <Box key={text} paddingX={1} paddingY={0}>
                                <ProtectedAction {...buttonsProps[index]}>
                                    {text}
                                </ProtectedAction>
                            </Box>
                        ) : null
                    ))}
                </Box>
            )}
            {children}
            {!isDesktop && isAnyButtonShown && (
                <FixedButtonWrapper>
                    {actions.map(({ isShown = true, text }, index) => (
                        isShown ? (
                            <Box key={text} paddingY={1} paddingX={0}>
                                <ProtectedAction {...buttonsProps[index]}>
                                    {text}
                                </ProtectedAction>
                            </Box>
                        ) : null
                    ))}
                </FixedButtonWrapper>
            )}
        </PageWrapper>
    );
};

export default WarehousePageWrapper;
